import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {Transaction} from "@/integrations/supabase/transaction.models.ts";
import {computeCategoryBreakdown, filterByDateRange} from "@transactionUtils";

/**
 * Transaction service managing transactions
 */
export class TransactionService extends BaseService {

    /**
     * Creates a new transaction and returns it
     * @param {TransactionCreateRequest} request
     */
    async createTransaction(request: TransactionCreateRequest): Promise<Transaction> {
        const txPayload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('transactions')
                .insert(txPayload)
                .select()
                .single();

            if (error) {
                await this.handleError(error);
            }
            return data;
        } catch (error) {
            await this.handleError(error);
        }
    }

    /**
     * Fetches transaction breakdown by type and period
     * @param {string} userId The user to filter records for
     * @param {CashFlowType} type The type of the cash flow for transactions to filter by
     * @param {PeriodFilter} period The period to filter transactions by
     */
    async fetchTransactionBreakdownByType(userId: string, type: CashFlowType, period: PeriodFilter) {
        const { data, error } = await supabase
            .from('transactions')
            .select(
                'category_id, sub_category_id, amount, currency, type, transaction_date, categories(name), sub_categories(name)'
            )
            .eq('user_id', userId)
            .eq('type', type);

        if (error) {
            await this.handleError(error);
            throw error;
        }

        const transactions = data.map((t) => ({
            categoryId: t.category_id ?? '',
            categoryName: t.categories?.name ?? 'Uncategorized',
            subCategoryId: t.sub_category_id ?? null,
            subCategoryName: t.sub_categories?.name ?? null,
            amount: t.amount,
            currency: t.currency,
            type: t.type as CashFlowType,
            transactionDate: t.transaction_date,
        }));

        const filtered = filterByDateRange(transactions, period.dateFrom, period.dateTo);
        return computeCategoryBreakdown(filtered, type);
    }
}

export default new TransactionService();
